import { MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  IPv4DetailsCell,
  ProgressBarIPVGroups,
} from '@/erp/sytem-sttings/ipv4-groups/shared/components';
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  emptyCellOneLevel,
  formatDateWithTimeCell,
  hasAllPermissions,
  hasPermission,
} from '@/shared';
import { useFetchIpv6Groups, useUpdateIpv6Group } from '@/shared/actions/erp';
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from '@/shared/components/ui';
import {
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
} from '@/shared/hooks';
import { Ipv6Group } from '@/shared/interfaces/erp/system-settings';
import { useUiConfirmModalStore } from '@/store/ui';


export type Ipv6GroupsPageProps = {};

const Ipv6GroupsPage: React.FC<Ipv6GroupsPageProps> = () => {
  useCheckPermissions(PermissionsEnum.api_view_grupoipv6);
  const returnUrlIPv6Page = ROUTER_PATHS.configuracionRED.grupoIpv6Nav;

  const navigate = useNavigate();

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: ipv6groupsPagingRes,
    isLoading,
    isRefetching,
  } = useFetchIpv6Groups({
    page: pageIndex + 1,
    page_size: pageSize,
    nombre: searchTerm,
    ...filterObject,
    filterByState: false,
  });

  ///* mutations
  const updSate = useUpdateIpv6Group({
    enableRedirect: false,
  });

  ///* columns
  const columns = useMemo<MRT_ColumnDef<Ipv6Group>[]>(
    () => [
      {
        accessorKey: 'nombre',
        header: 'Nombre',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'nombre'),
      },
      {
        accessorKey: 'ipv_6',
        header: 'IPv6',
        size: 360,
        Cell: ({ row }) => emptyCellOneLevel(row, 'ipv_6'),
      },

      {
        accessorKey: 'uso',
        header: 'Uso IPS',
        size: 267,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <ProgressBarIPVGroups ipDisponibles={row.original?.ips_disponibles} />
        ),
      },
      {
        accessorKey: 'detalle',
        header: 'Detalle',
        size: 60,
        Cell: ({ row }) => (
          <IPv4DetailsCell availableIps={row.original?.ips_disponibles || []} />
        ),
      },

      {
        accessorKey: 'cidr',
        header: 'Máscara',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'cidr'),
      },

      {
        accessorKey: 'router',
        header: 'Router',
        size: 222,
      },
      {
        accessorKey: 'tipo_uso',
        header: 'Tipo de uso',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'tipo_uso'),
      },

      {
        accessorKey: 'codigo',
        header: 'Código',
        size: 200,
        Cell: ({ row }) => emptyCellOneLevel(row, 'codigo'),
      },

      {
        accessorKey: 'estado',
        header: 'Estado',
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original.estado}
              titulo={row.original.estado ? 'Activo' : 'Inactivo'}
              setEstadoSwitch={state => {
                if (!hasPermission(PermissionsEnum.api_change_grupoipv6))
                  return;

                setConfirmDialog({
                  isOpen: true,
                  title: 'Cambiar Estado',
                  subtitle:
                    '¿Está seguro que desea cambiar el estado de este registro ?',
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updSate.mutate({
                      id: row.original.id_grupoipv6!,
                      data: { estado: !!state } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },
      {
        accessorKey: 'fecha_creacion',
        header: 'Fecha de Creación',
        size: 200,
        enableColumnFilter: true,
        filterVariant: 'date',
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_creacion'),
      },
      {
        accessorKey: 'fecha_modificacion',
        header: 'Fecha de Modificación',
        size: 200,
        enableColumnFilter: true,
        filterVariant: 'date',
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, 'fecha_modificacion'),
      },
    ],
    []
  );

  ///* handlers
  const onEdit = (row: Ipv6Group) => {
    navigate(`${returnUrlIPv6Page}/editar/${row.id_grupoipv6}`);
  };

  return (
    <SingleTableBoxScene
      title="Grupos IPv6"
      urlRedirect={`${returnUrlIPv6Page}/crear`}
      enableRedirect={hasAllPermissions([
        PermissionsEnum.api_change_sector,
        PermissionsEnum.api_view_router,
      ])}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="grupo nombre"
      />

      <GeneralTable<Ipv6Group>
        columns={columns}
        data={ipv6groupsPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // filters - llega al back
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={ipv6groupsPagingRes?.data?.count}
        // // actions ========
        enableColumnActions={hasAllPermissions([
          PermissionsEnum.api_change_sector,
          PermissionsEnum.api_view_router,
        ])}
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        // crud
        canEdit={hasAllPermissions([
          PermissionsEnum.api_change_sector,
          PermissionsEnum.api_view_router,
        ])}
        onEdit={onEdit}
        canDelete={false}
      />
    </SingleTableBoxScene>
  );
};

export default Ipv6GroupsPage;
